import { useEffect, useRef, useState } from "react";
import { motion } from "motion/react";
import { ArrowDown, ExternalLink } from "lucide-react";

// Fallback roles if personalInfo title is barely descriptive
const FALLBACK_ROLES_FR = [
  "Développeur Full-Stack",
  "Architecte de solutions",
  "Créateur d'expériences",
  "Passionné du code",
];

const FALLBACK_ROLES_EN = [
  "Full-Stack Developer",
  "Solutions Architect",
  "Experience Creator",
  "Code Enthusiast",
];

export function Hero({ personalInfo, language = "fr" }: { personalInfo?: any, language?: string }) {
  const roles = personalInfo?.title
    ? [
      personalInfo.title,
      language === "en" ? "Tech Enthusiast" : "Passionné de technologie",
      language === "en" ? "Experience Creator" : "Créateur d'expériences",
    ]
    : language === "en"
      ? FALLBACK_ROLES_EN
      : FALLBACK_ROLES_FR;

  const GLITCH_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*<>/\\|";

  function useScramble(text: string, trigger: boolean) {
    const [display, setDisplay] = useState(text);
    const frameRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
      if (!trigger) return;
      let i = 0;
      if (frameRef.current) clearInterval(frameRef.current);
      frameRef.current = setInterval(() => {
        setDisplay(
          text
            .split("")
            .map((ch, idx) => {
              if (ch === " ") return " ";
              if (idx < i) return ch;
              return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
            })
            .join("")
        );
        i += 0.5;
        if (i > text.length) {
          if (frameRef.current) clearInterval(frameRef.current);
          setDisplay(text);
        }
      }, 30);
      return () => {
        if (frameRef.current) clearInterval(frameRef.current);
      };
    }, [trigger, text]);

    return display;
  }

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [roleIdx, setRoleIdx] = useState(0);
  const [typedRole, setTypedRole] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [scrambleTrigger, setScrambleTrigger] = useState(false);

  const displayName = personalInfo?.name?.toUpperCase() || "SAMI CAKIRAL";
  const nameScramble = useScramble(displayName, scrambleTrigger);

  // Trigger scramble on mount
  useEffect(() => {
    const t = setTimeout(() => setScrambleTrigger(true), 400);
    return () => clearTimeout(t);
  }, []);

  // Canvas constellation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    const particles: { x: number; y: number; vx: number; vy: number; r: number }[] = [];
    const N = 60;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();

    for (let i = 0; i < N; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 1.5 + 0.5,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(34,211,238,0.5)";
        ctx.fill();
      });

      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 130) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(34,211,238,${0.12 * (1 - dist / 130)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animId = requestAnimationFrame(draw);
    };
    draw();

    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  // Typing effect
  useEffect(() => {
    const current = roles[roleIdx] || "";
    let timeout: ReturnType<typeof setTimeout>;

    if (!isDeleting && typedRole.length < current.length) {
      timeout = setTimeout(() => setTypedRole(current.slice(0, typedRole.length + 1)), 60);
    } else if (!isDeleting && typedRole.length === current.length) {
      timeout = setTimeout(() => setIsDeleting(true), 2200);
    } else if (isDeleting && typedRole.length > 0) {
      timeout = setTimeout(() => setTypedRole(current.slice(0, typedRole.length - 1)), 35);
    } else if (isDeleting && typedRole.length === 0) {
      setIsDeleting(false);
      setRoleIdx((prev) => (prev + 1) % roles.length);
    }
    return () => clearTimeout(timeout);
  }, [typedRole, isDeleting, roleIdx, roles]);

  const scrollDown = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      className="relative min-h-screen flex flex-col justify-center items-start overflow-hidden"
      style={{ background: "#050508" }}
    >
      <canvas ref={canvasRef} className="absolute inset-0 z-0" />

      {/* Radial glow */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(34,211,238,0.04) 0%, transparent 70%)",
        }}
      />

      {/* Scanlines */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-20"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)",
        }}
      />

      {/* Grid */}
      <div
        className="absolute inset-0 z-0 pointer-events-none opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(rgba(34,211,238,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(34,211,238,0.5) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-24">
        {/* System tag */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="flex items-center gap-3 mb-8"
        >
          <div
            className="px-3 py-1 rounded-full flex items-center gap-2"
            style={{
              background: "rgba(34,211,238,0.08)",
              border: "1px solid rgba(34,211,238,0.2)",
            }}
          >
            <span
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ background: "#22d3ee", boxShadow: "0 0 6px #22d3ee" }}
            />
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "11px",
                color: "#22d3ee",
                letterSpacing: "0.1em",
              }}
            >
              {language === "en" ? "SYSTEM OPERATIONAL — v2.0.26" : "SYSTÈME OPÉRATIONNEL — v2.0.26"}
            </span>
          </div>
        </motion.div>

        {/* Main name */}
        <div className="overflow-hidden mb-4">
          <motion.h1
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "clamp(52px, 10vw, 140px)",
              fontWeight: 700,
              lineHeight: 1,
              color: "#ffffff",
              letterSpacing: "-0.03em",
            }}
          >
            {nameScramble}
          </motion.h1>
        </div>

        {/* Role typing */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="flex items-center gap-3 mb-12"
        >
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "clamp(14px, 2.5vw, 22px)",
              color: "rgba(255,255,255,0.4)",
            }}
          >
            ~/
          </span>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "clamp(14px, 2.5vw, 22px)",
              color: "#a78bfa",
            }}
          >
            {typedRole}
            <span
              className="inline-block ml-0.5 animate-pulse"
              style={{ color: "#22d3ee" }}
            >
              ▌
            </span>
          </span>
        </motion.div>

        {/* Tagline */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.7 }}
          style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: "clamp(15px, 2vw, 20px)",
            color: "rgba(255,255,255,0.45)",
            maxWidth: "560px",
            lineHeight: 1.65,
          }}
        >
          {personalInfo?.summary || (language === "en" ? "I transform complex ideas into memorable digital experiences — from design to production." : "Je transforme des idées complexes en expériences numériques mémorables — du design à la mise en production.")}
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.7 }}
          className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 mt-10"
        >
          <a
            href={`/dev?lang=${language}`}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 px-7 py-3.5 rounded-full transition-all"
            style={{
              background: "linear-gradient(135deg, #22d3ee, #a78bfa)",
              color: "#050508",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
              fontWeight: 600,
              boxShadow: "0 0 30px rgba(34,211,238,0.3)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 0 50px rgba(34,211,238,0.5)";
              e.currentTarget.style.transform = "scale(1.03)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 0 30px rgba(34,211,238,0.3)";
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <ExternalLink size={16} />
            {language === "en" ? "Open Editor" : "Ouvrir l'éditeur"}
          </a>

          <a
            href={personalInfo?.cv_link || "#"} // Assuming personalInfo has a cv_link
            target="_blank"
            rel="noopener noreferrer"
            className="group relative flex items-center justify-center px-7 py-3.5 rounded-full transition-all overflow-hidden"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.7)",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(34,211,238,0.4)";
              e.currentTarget.style.color = "#22d3ee";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
              e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            }}
          >
            <span className="relative font-['JetBrains_Mono'] tracking-wide text-[13px] text-white/70 group-hover:text-white transition-colors">
              {language === "en" ? "Download CV" : "Télécharger CV"}
            </span>
          </a>

          <button
            onClick={scrollDown}
            className="flex items-center gap-2 px-7 py-3.5 rounded-full transition-all"
            style={{
              background: "transparent",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.7)",
              fontFamily: "'Space Grotesk', sans-serif",
              fontSize: "15px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "rgba(34,211,238,0.4)";
              e.currentTarget.style.color = "#22d3ee";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)";
              e.currentTarget.style.color = "rgba(255,255,255,0.7)";
            }}
          >
            {language === "en" ? "Explore" : "Explorer"}
          </button>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.7 }}
          className="flex flex-wrap items-center gap-8 mt-16 pt-10"
          style={{ borderTop: "1px solid rgba(255,255,255,0.05)" }}
        >
          {[
            { value: personalInfo?.years_of_experience ? `${personalInfo.years_of_experience}+` : "5+", label: language === "en" ? "years of exp" : "ans d'xp" },
            { value: "30+", label: language === "en" ? "projects" : "projets" },
            { value: "∞", label: language === "en" ? "coffee consumed" : "café bu" },
          ].map((s) => (
            <div key={s.label} className="flex flex-col">
              <span
                style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: "32px",
                  fontWeight: 700,
                  color: "#22d3ee",
                  lineHeight: 1,
                }}
              >
                {s.value}
              </span>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.35)",
                  letterSpacing: "0.1em",
                  marginTop: "4px",
                }}
              >
                {s.label}
              </span>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ boxShadow: "0 0 8px #34d399" }} />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "11px", letterSpacing: "0.06em", color: "rgba(255,255,255,0.7)" }}>
              {language === "en" ? "Available now" : "Disponible actuellement"}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Scroll arrow */}
      <motion.button
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        style={{ background: "none", border: "none" }}
      >
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: "10px",
            color: "rgba(255,255,255,0.3)",
            letterSpacing: "0.15em",
          }}
        >
          SCROLL
        </span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} color="rgba(255,255,255,0.3)" />
        </motion.div>
      </motion.button>
    </section>
  );
}
